type ValueType = number | string;

export const BASELINE_REM = 16;

export const isPxString = (value: string) => {
  return typeof value === 'string' && value.endsWith('px');
};

export const normalizeValue = (value: any) => {
  if (isPxString(value)) return trimPx(value);
  if (typeof value !== 'number') return value;
  // splitting this out because maybe we also want to add in some erroring
  return value;
};

export const px = (value: any) => {
  if (typeof value !== 'number') return value;
  return `${value}px`;
};

export const trimPx = (value: string) => {
  return Number(value.slice(0, -2));
};

/**
 * remify
 * takes a pixel value (and optionally a min and max) and converts it to a rem value.
 *
 * Having a min and max value is handy to ensure that there's some flexibility in responsive UI sizing, but not to the
 * point where things are drastically unusable.
 *
 * For example, for `1px` values used in borders. They should not be less than `1px`. However, they can be more than
 * `1px` if necessary.
 */
export const remify = (
  value: ValueType = BASELINE_REM,
  min?: ValueType,
  max?: ValueType
) => {
  const baseValue = normalizeValue(value);

  const size = `calc(${baseValue} / ${BASELINE_REM} * 1rem)`;
  if (!min && !max) {
    return size;
  }

  if (min && !max) {
    return `min(${size}, ${px(min)})`;
  }
  if (min && max) {
    return `clamp(${size}, ${px(min)}, ${px(max)})`;
  }
};

/**
 * batchRemify
 * takes an object of px values and converts them to rems, leaving original keys intact
 */
export const batchRemify = (obj: Record<any, any>) => {
  return Object.entries(obj).reduce<Record<string, any>>(
    (acc, [key, value]: [any, any]) => {
      if (isPxString(value)) {
        acc[key] = remify(trimPx(value));
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};
